/**
 * Phase J-UX — Time-bucket grouping for the journal timeline.
 *
 * Groups entries into:
 *   Today · Yesterday · This week · Last week · <Month Year>
 *
 * Pure helper. Caller passes the chronologically-sorted entries
 * array and renders a section header + group of cards per bucket.
 */
function startOfDay(d) {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function startOfThisWeek() {
  const d = startOfDay(new Date());
  const day = d.getDay(); // 0 = Sun
  const offset = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - offset);
  return d;
}

export function bucketEntries(entries) {
  if (!entries || entries.length === 0) return [];
  const today = startOfDay(new Date());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekStart = startOfThisWeek();
  const lastWeekStart = new Date(weekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);

  const buckets = new Map();
  // Preserve a stable bucket order — we use an integer key prefix.
  const labelFor = (e) => {
    if (!e.created_at) return { key: "9999_unknown", label: "Earlier" };
    const d = startOfDay(new Date(e.created_at));
    if (d.getTime() === today.getTime()) return { key: "0001_today", label: "Today" };
    if (d.getTime() === yesterday.getTime())
      return { key: "0002_yesterday", label: "Yesterday" };
    if (d >= weekStart) return { key: "0003_this_week", label: "This week" };
    if (d >= lastWeekStart) return { key: "0004_last_week", label: "Last week" };
    // Month / year — older first means "May 2026" < "April 2026" in
    // calendar terms, but we want newer months on top, so reverse-
    // ordered numeric key derived from the unix epoch milliseconds.
    const m = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    const key = "1000_" + String(2_100_000 - Math.floor(d.getTime() / 1_000_000)).padStart(10, "0");
    return { key, label: m };
  };

  for (const e of entries) {
    const { key, label } = labelFor(e);
    if (!buckets.has(key)) buckets.set(key, { key, label, entries: [] });
    buckets.get(key).entries.push(e);
  }
  // Sort by the stable key.
  return Array.from(buckets.values()).sort((a, b) =>
    a.key.localeCompare(b.key),
  );
}
